import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/contexts/LanguageContext";
import NewHeader from "@/components/NewHeader";
import Footer from "@/components/Footer";
import { getPostBySlug, JournalPost } from "@/lib/journal";
import { SEOLanguage } from "@/data/seoTranslations";
import { Loader2, ArrowLeft, Calendar, User, Clock, Share2, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Breadcrumbs from "@/components/Breadcrumbs";
import { JournalImage, JournalQuote, JournalCallout, JournalDivider, JournalList, JournalTable, JournalCTA } from "@/components/journal/JournalComponents";
import { JournalAccordion } from "@/components/journal/JournalAccordion";
import OptimizedImage from "@/components/OptimizedImage";
import { GalleryGrid } from "@/components/ui/gallery-grid";
import NotFound from "./NotFound";

const JournalPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [post, setPost] = useState<JournalPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;
      
      setLoading(true);
      try {
        const fetchedPost = await getPostBySlug(slug, language as SEOLanguage);
        setPost(fetchedPost || null);
      } catch (error) {
        console.error("Failed to load post:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug, language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-alpine-gold" />
      </div>
    );
  }

  if (!post) {
    return <NotFound />;
  }

  const { title, description, date, image, author, category, keywords, faqs } = post.meta;
  const ContentComponent = post.component;

  const formattedDate = new Date(date).toLocaleDateString(
    language === "de" ? "de-CH" : language === "fr" ? "fr-CH" : "en-US",
    { year: "numeric", month: "long", day: "numeric" }
  );

  const journalLink = language === "en" ? "/journal" : `/${language}/journal`;

  // Provide components to MDX
  const components = {
    img: (props: any) => <JournalImage {...props} />,
    blockquote: (props: any) => <JournalQuote {...props} />,
    JournalImage,
    JournalQuote,
    JournalCallout,
    JournalDivider,
    JournalList,
    JournalTable,
    JournalAccordion,
    JournalCTA,
    GalleryGrid
  };

  // Generate Structured Data (BlogPosting)
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "image": [`https://beatenberg-resortspa.ch${image}`],
    "datePublished": date,
    "dateModified": date,
    "author": [{
      "@type": "Person",
      "name": author,
      "url": "https://beatenberg-resortspa.ch"
    }],
    "publisher": {
      "@type": "Organization",
      "name": "Beatenberg Resort & Spa",
      "logo": {
        "@type": "ImageObject",
        "url": "https://beatenberg-resortspa.ch/images/phb-icon-180.png"
      }
    },
    "description": description,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://beatenberg-resortspa.ch/journal/${post.slug}`
    }
  };

  const schemas = [blogSchema];

  if (faqs && faqs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    });
  }

  return (
    <div className="min-h-screen bg-white font-sans selection:bg-alpine-gold/30">
      <Helmet>
        <title>{title} | Beatenberg Resort & Spa</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords.join(", ")} />}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <script type="application/ld+json">
          {JSON.stringify(schemas)}
        </script>
      </Helmet>

      <NewHeader />

      <article>
        {/* Full Screen Hero Image */}
        <div className="relative h-[60vh] min-h-[500px] w-full">
          <div className="absolute inset-0">
            <OptimizedImage
              src={image}
              alt={title}
              className="w-full h-full"
              priority={true}
              fetchPriority="high"
              hideLoadingSpinner={true}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          </div>
          
                  <div className="absolute bottom-0 left-0 right-0 pb-16 pt-32 px-4">
                    <div className="container mx-auto max-w-4xl">
                      <Breadcrumbs />
                      <div className="flex flex-wrap items-center gap-4 mb-6">
                <Badge className="bg-alpine-gold text-white border-none uppercase tracking-widest text-xs px-3 py-1 rounded-sm">
                  {category}
                </Badge>
                <span className="text-white/80 text-sm uppercase tracking-wide font-medium flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  {formattedDate}
                </span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-playfair font-bold text-white mb-6 leading-tight drop-shadow-md">
                {title}
              </h1>
              
              <div className="flex items-center text-white/90">
                <span className="font-playfair italic text-lg mr-2">by</span>
                <span className="uppercase tracking-widest text-sm font-medium border-b border-alpine-gold pb-0.5">{author}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Article Body */}
        <div className="container mx-auto px-4 max-w-4xl py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Sidebar / Social Share */}
            <div className="lg:col-span-1 hidden lg:block">
              <div className="sticky top-32 flex flex-col gap-4">
                <Button variant="outline" size="icon" className="rounded-full border-gray-200 hover:border-alpine-gold hover:text-alpine-gold">
                  <Share2 className="w-4 h-4" />
                </Button>
                <Link to={journalLink}>
                   <Button variant="ghost" size="icon" className="rounded-full text-gray-400 hover:text-gray-900">
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-10">
              <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed mb-12 border-l-4 border-alpine-gold pl-6 italic">
                {description}
              </p>
              
              <div className="prose prose-lg md:prose-xl max-w-none 
                prose-headings:font-playfair prose-headings:font-bold prose-headings:text-gray-900
                prose-p:text-gray-600 prose-p:font-light prose-p:leading-relaxed
                prose-a:text-alpine-gold prose-a:no-underline hover:prose-a:text-pine-green hover:prose-a:underline
                prose-strong:font-semibold prose-strong:text-gray-900
                prose-img:rounded-sm prose-img:shadow-lg prose-img:my-12
                prose-blockquote:border-l-alpine-gold prose-blockquote:bg-gray-50 prose-blockquote:py-6 prose-blockquote:px-8 prose-blockquote:italic prose-blockquote:text-gray-700
                first-letter:float-left first-letter:text-7xl first-letter:pr-4 first-letter:font-playfair first-letter:text-alpine-gold first-letter:leading-[0.8]">
                <ContentComponent components={components} />
              </div>

              {/* Tags */}
              {keywords && keywords.length > 0 && (
                <div className="mt-16 pt-8 border-t border-gray-100">
                  <div className="flex flex-wrap gap-2">
                    <Tag className="w-4 h-4 text-gray-400 mt-1" />
                    {keywords.map(keyword => (
                      <span key={keyword} className="text-xs uppercase tracking-wider text-gray-500 bg-gray-50 px-3 py-1.5 rounded-sm border border-gray-100">
                        #{keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Back Button Mobile */}
              <div className="mt-12 lg:hidden">
                 <Button variant="outline" asChild className="w-full justify-center">
                  <Link to={journalLink}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Journal
                  </Link>
                </Button>
              </div>
            </div>
            
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default JournalPostPage;
